import React, {useState} from "react";
import useApiStable from "../../utils/useApiStable";
import UnifiedMatchResultInput from "./UnifiedMatchResultInput";
import GroupPhaseMatchTableRow from "./GroupPhaseMatchTableRow";
import {useAuth} from "../../context/AuthContext";
import {isUserAdmin} from "../../utils/userUtils";


const GroupPhaseMatchTable = ({
                                  groupPlayers,
                                  players,
                                  selectedFilterPlayerId,
                                  findMatch,
                                  setEditingMatch,
                                  setSchedulerPair,
                                  reloadMatches,
                                  selectedGroupId, // Neue Prop für die groupId
                              }) => {
    const {user} = useAuth();
    const api = useApiStable();
    const [directEntryModal, setDirectEntryModal] = useState(null); // State für den modalen Dialog
    const userIsAdmin = isUserAdmin(user);

    const participantContainsCurrentUser = (participant) => {
        if (!participant || !user) return false;

        if (participant.personId != null && user.personId != null) {
            return Number(participant.personId) === Number(user.personId);
        }

        if (Array.isArray(participant.players)) {
            return participant.players.some(
                (player) => {
                    if (!player) return false;

                    if (player.personId != null && user.personId != null) {
                        return Number(player.personId) === Number(user.personId);
                    }

                    if (user.name && player.name) {
                        return player.name.trim().toLowerCase() === user.name.trim().toLowerCase();
                    }

                    return false;
                }
            );
        }

        if (user.name && participant.name) {
            return participant.name.trim().toLowerCase() === user.name.trim().toLowerCase();
        }

        return false;
    };

    const canDeleteMatch = (participant1, participant2, match) => {
        if (userIsAdmin) return true;
        // Vergangene Matches dürfen nur von Admins gelöscht werden
        if (match?.startTime && new Date(match.startTime) < new Date()) return false;
        return participantContainsCurrentUser(participant1)
            || participantContainsCurrentUser(participant2);
    };

    const canEditResult = (participant1, participant2) => {
        if (userIsAdmin) return true;
        return participantContainsCurrentUser(participant1)
            || participantContainsCurrentUser(participant2);
    };

    // Hilfsfunktion: Match löschen
    const handleDeleteMatch = async (match) => {
        if (!match?.id) return;

        const hasResult = match.sets && match.sets.length > 0;
        const message = hasResult
            ? "Soll das Ergebnis dieser Begegnung wirklich gelöscht werden? Die Planung bleibt bestehen."
            : "Soll diese geplante Begegnung wirklich gelöscht werden?";

        if (window.confirm(message)) {
            if (hasResult) {
                // Nur das Ergebnis (die Sätze) löschen
                await api.delete(`/tournaments/matches/${match.id}/sets`);
            } else {
                // Das gesamte Match-Objekt löschen
                const result = await api.delete(`/tournaments/matches/${match.id}`);
                if (result?.warning) {
                    alert(result.warning);
                }
            }
            if (typeof reloadMatches === "function") reloadMatches();
        }
    };

    // Neue Funktion für direkte Ergebniseingabe - öffnet modalen Dialog
    const handleDirectResultEntry = (player1, player2) => {
        setDirectEntryModal({player1, player2});
    };

    // Funktion zum Speichern des direkten Ergebnisses
    const handleDirectResultSave = async (matchData) => {
        try {
            // Erstelle Match mit allen Daten
            const newMatch = await api.post("/tournaments/matches", {
                team1Id: matchData.team1Id,
                team2Id: matchData.team2Id,
                startTime: matchData.date || null,
                endTime: matchData.endTime || null,
                courtId: matchData.courtId || null,
                court: matchData.court || null,
                groupId: selectedGroupId,
                // Reine Ergebniseintragung soll keine Tennis04-Buchung auslösen
                createBooking: false,
            });

            // Speichere die Sätze
            if (matchData.setResults && matchData.setResults.length > 0) {
                await api.put(`/tournaments/matches/${newMatch.id}/sets`, matchData.setResults);
            }

            if (typeof reloadMatches === "function") reloadMatches();
        } catch (err) {
            console.error("Fehler beim Erstellen der Begegnung:", err);
            throw new Error("Fehler beim Erstellen der Begegnung: " + err.message);
        }
    };

    const handleScheduleMatch = (participant1, participant2) => {
        setSchedulerPair({
            player1: participant1,
            player2: participant2,
        });
    };

    const selectedMainPlayer = selectedFilterPlayerId
        ? groupPlayers.find((player) => player.id === Number(selectedFilterPlayerId))
        : null;
    const rowConfigurations = selectedMainPlayer
        ? groupPlayers
            .filter((player) => player.id !== selectedMainPlayer.id)
            .map((otherPlayer) => ({
                participant1: selectedMainPlayer,
                participant2: otherPlayer,
                match: findMatch(selectedMainPlayer.id, otherPlayer.id),
                useParticipantPerspective: true,
            }))
        : groupPlayers.flatMap((participant1, index) =>
            groupPlayers.slice(index + 1).map((participant2) => ({
                participant1,
                participant2,
                match: findMatch(participant1.id, participant2.id),
                useParticipantPerspective: false,
            }))
        );

    const anyMatchHasResult = rowConfigurations.some(({match}) => match?.sets?.length > 0);
    const playedRows = rowConfigurations.filter(({match}) => match?.sets?.length > 0);
    const openRows = rowConfigurations.filter(({match}) => !match?.sets?.length);

    return (
        <div className="groupphase-table-container">
            {/* Offene / geplante Begegnungen */}
            {openRows.length > 0 && (
            <table className="groupphase-table">
                <thead>
                <tr>
                    <th>Team 1</th>
                    <th>Team 2</th>
                    <th>Datum</th>
                    <th>Platz</th>
                    <th>Aktion</th>
                </tr>
                </thead>
                <tbody>
                {openRows.map(({participant1, participant2, match, useParticipantPerspective}) => (
                    <GroupPhaseMatchTableRow
                        key={`${participant1.id}-${participant2.id}`}
                        participant1={participant1}
                        participant2={participant2}
                        match={match}
                        showSetColumns={false}
                        deleteAllowed={canDeleteMatch(participant1, participant2, match)}
                        resultAllowed={canEditResult(participant1, participant2)}
                        isAdminUser={userIsAdmin}
                        useParticipantPerspective={useParticipantPerspective}
                        onEditMatch={setEditingMatch}
                        onDeleteMatch={handleDeleteMatch}
                        onScheduleMatch={handleScheduleMatch}
                        onDirectResultEntry={handleDirectResultEntry}
                    />
                ))}
                </tbody>
            </table>
            )}

            {/* Gespielte Begegnungen */}
            {playedRows.length > 0 && (
                <>
                    <h3 style={{marginTop: 16, marginBottom: 6, fontSize: "1rem", color: "#555"}}>Ergebnisse</h3>
                    <table className="groupphase-table">
                        <thead>
                        <tr>
                            <th>Team 1</th>
                            <th>Team 2</th>
                            <th>Satz 1</th>
                            <th>Satz 2</th>
                            <th>Satz 3</th>
                            <th>Aktion</th>
                        </tr>
                        </thead>
                        <tbody>
                        {playedRows.map(({participant1, participant2, match, useParticipantPerspective}) => (
                            <GroupPhaseMatchTableRow
                                key={`${participant1.id}-${participant2.id}`}
                                participant1={participant1}
                                participant2={participant2}
                                match={match}
                                showSetColumns={true}
                                deleteAllowed={canDeleteMatch(participant1, participant2, match)}
                                resultAllowed={canEditResult(participant1, participant2)}
                                isAdminUser={userIsAdmin}
                                useParticipantPerspective={useParticipantPerspective}
                                onEditMatch={setEditingMatch}
                                onDeleteMatch={handleDeleteMatch}
                                onScheduleMatch={handleScheduleMatch}
                                onDirectResultEntry={handleDirectResultEntry}
                            />
                        ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* Modaler Dialog für direkte Ergebniseingabe */}
            {directEntryModal && (
                <UnifiedMatchResultInput
                    player1={directEntryModal.player1}
                    player2={directEntryModal.player2}
                    groupId={selectedGroupId}
                    onClose={() => setDirectEntryModal(null)}
                    onSave={handleDirectResultSave}
                    players={players}
                    mode="overlay"
                    title="Ergebnis"
                    isDirectEntry={true}
                    allowVariableSets={false}
                    requireWinner={false}
                />
            )
            }
        </div>
    )
        ;
};

export default GroupPhaseMatchTable;
